import { Category } from '../types';
import { OperationNotification } from '../types/operations';

export function categoryOptions(categories: Category[]) {
  const byId = new Map(categories.map((category) => [category.id, category]));
  const options = categories.map((category) => {
    const ancestors: Category[] = [];
    const seen = new Set([category.id]);
    let parent = category.parentId;
    while (parent && !seen.has(parent)) {
      seen.add(parent);
      const item = byId.get(parent);
      if (!item) break;
      ancestors.unshift(item);
      parent = item.parentId;
    }
    return {
      id: category.id,
      name: category.name,
      path: [...ancestors, category].map((item) => item.name).join(' › '),
      ancestors: ancestors.map((item) => item.id),
      depth: ancestors.length,
      order: [...ancestors, category]
        .map((item) => String(item.weight ?? 0).padStart(8, '0') + item.name)
        .join('/'),
    };
  });
  return options.sort((a, b) => a.order.localeCompare(b.order));
}

export function toggleCategory(
  ids: string[],
  id: string,
  categories: Category[],
) {
  if (ids.includes(id)) return ids.filter((value) => value !== id);
  const descendants = new Set(
    categoryOptions(categories)
      .filter((item) => item.ancestors.includes(id))
      .map((item) => item.id),
  );
  return [...ids.filter((value) => !descendants.has(value)), id];
}

export function notificationStoreId(href: string): string | null {
  const match = /^\/\(app\)\/stores\?storeId=([^&#]+)/.exec(href);
  try {
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

export function notificationTaskId(eventId?: string): string | null {
  return (
    /^task:(.+):[0-9]+:[0-9]+$/.exec(eventId ?? '')?.[1] ??
    /^overdue:(.+):[0-9]+$/.exec(eventId ?? '')?.[1] ??
    null
  );
}

export function groupNotifications(notifications: OperationNotification[]) {
  const groups = new Map<
    string,
    {
      id: string;
      storeId: string | null;
      taskId: string | null;
      items: OperationNotification[];
      unread: number;
      missing: number;
      expired: number;
      upcoming: number;
    }
  >();
  for (const notification of notifications.filter((item) => item.active)) {
    const storeId = notificationStoreId(notification.href);
    const taskId = notificationTaskId(notification.eventId);
    const id = storeId
      ? 'store:' + storeId
      : taskId
        ? 'task:' + taskId
        : 'other';
    const group = groups.get(id) ?? {
      id,
      storeId,
      taskId,
      items: [],
      unread: 0,
      missing: 0,
      expired: 0,
      upcoming: 0,
    };
    group.items.push(notification);
    if (!notification.read) group.unread++;
    if (notification.eventId?.startsWith('batch:')) {
      if (notification.eventId.endsWith(':unknown')) group.missing++;
      else if (notification.eventId.endsWith(':-1')) group.expired++;
      else group.upcoming++;
    }
    groups.set(id, group);
  }
  return [...groups.values()].sort(
    (a, b) => b.unread - a.unread || a.id.localeCompare(b.id),
  );
}

export function taskFormErrors(
  form: Record<string, string>,
  scope: string,
  categoryIds: string[],
) {
  const errors: Record<string, string> = {};
  if (!form.storeId) errors.storeId = 'taskStoreRequired';
  if (!form.titleLabel?.trim()) errors.titleLabel = 'taskTitleRequired';
  const date = form.dueOn ?? '';
  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(date) ||
    !Number.isFinite(Date.parse(date)) ||
    new Date(date).toISOString().slice(0, 10) !== date
  )
    errors.dueOn = 'taskDueRequired';
  const interval = Number(form.interval);
  if (
    form.recurring === 'yes' &&
    (!Number.isInteger(interval) || interval < 1 || interval > 3650)
  )
    errors.interval = 'taskIntervalInvalid';
  if (
    form.type === 'stocktaking' &&
    scope === 'selected' &&
    !categoryIds.length
  )
    errors.scope = 'taskScopeRequired';
  return errors;
}
